import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Copy, Users, MousePointerClick, Euro, Coins, Share2, Check } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const T = {
  ro: { title: "Program de Parteneriat", sub: "Recomandă OXO și câștigă comisioane.", link: "Linkul tău de invitație",
    copy: "Copiază", copied: "Copiat!", clicks: "Click-uri", registered: "Utilizatori înregistrați",
    cash: "Comisioane câștigate", tokens: "Tokeni AI acumulați", how: "Cum funcționează",
    rules: ["30% comision din prima plată Pro a celor recomandați", "20% comision recurent la reînnoiri", "1% bonus din valoarea contractelor de utilaje închise prin platformă", "+20 tokeni pentru contribuții verificate"],
    history: "Istoric comisioane", empty: "Încă nu ai comisioane. Distribuie linkul tău!" },
  hu: { title: "Partneri Program", sub: "Ajánld az OXO-t és keress jutalékot.", link: "Meghívó linked",
    copy: "Másolás", copied: "Másolva!", clicks: "Kattintások", registered: "Regisztrált felhasználók",
    cash: "Megszerzett jutalék", tokens: "Összegyűjtött AI token", how: "Hogyan működik",
    rules: ["30% jutalék az ajánlottak első Pro fizetéséből", "20% ismétlődő jutalék a megújításokból", "1% bónusz a platformon zárt gép-szerződések értékéből", "+20 token ellenőrzött hozzájárulásokért"],
    history: "Jutalék előzmények", empty: "Még nincs jutalékod. Oszd meg a linkedet!" },
  en: { title: "Partner Program", sub: "Refer OXO and earn commissions.", link: "Your referral link",
    copy: "Copy", copied: "Copied!", clicks: "Clicks", registered: "Registered users",
    cash: "Earned commissions", tokens: "Accumulated AI tokens", how: "How it works",
    rules: ["30% commission on referred users' first Pro payment", "20% recurring commission on renewals", "1% bonus of closed machinery contract value via the platform", "+20 tokens for verified contributions"],
    history: "Commission history", empty: "No commissions yet. Share your link!" },
};

export default function Referral() {
  const { i18n } = useTranslation();
  const lng = (i18n.language || "ro").slice(0, 2);
  const t = T[lng] || T.ro;
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => { api.get("/referral/me").then((r) => setData(r.data)).catch(() => {}); }, []);

  const copy = () => {
    navigator.clipboard.writeText(data.link);
    setCopied(true); toast.success(t.copied); setTimeout(() => setCopied(false), 1800);
  };

  if (!data) return null;
  const stats = [
    { icon: MousePointerClick, label: t.clicks, value: data.clicks, testid: "ref-clicks" },
    { icon: Users, label: t.registered, value: data.registered_users, testid: "ref-registered" },
    { icon: Euro, label: t.cash, value: `€${data.earned_commission_eur}`, testid: "ref-cash" },
    { icon: Coins, label: t.tokens, value: data.earned_tokens, testid: "ref-tokens" },
  ];

  return (
    <div className="max-w-4xl mx-auto px-6 py-12" data-testid="referral-page">
      <div className="flex items-center gap-2"><Share2 className="w-6 h-6 text-orange-600" /><h1 className="text-3xl font-black tracking-tight">{t.title}</h1></div>
      <p className="text-slate-500 mt-2">{t.sub}</p>

      <div className="mt-6 border border-slate-200 p-5">
        <label className="text-[11px] uppercase tracking-wide font-bold text-slate-500">{t.link}</label>
        <div className="flex gap-2 mt-2">
          <input readOnly value={data.link} className="flex-1 border border-slate-300 px-3 py-2 text-sm font-mono bg-slate-50" data-testid="ref-link" />
          <button onClick={copy} className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 text-sm font-bold uppercase tracking-wide transition-colors" data-testid="ref-copy">
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} {copied ? t.copied : t.copy}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
        {stats.map((s) => (
          <div key={s.label} className="border border-slate-200 p-5" data-testid={s.testid}>
            <s.icon className="w-5 h-5 text-slate-400" />
            <div className="text-2xl font-black mt-2">{s.value}</div>
            <div className="text-[11px] uppercase tracking-wide text-slate-500 mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="mt-8 border border-slate-200 p-5">
        <h2 className="font-black text-lg">{t.how}</h2>
        <ul className="mt-3 space-y-2">
          {t.rules.map((r) => (
            <li key={r} className="flex items-start gap-2 text-sm text-slate-700"><Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />{r}</li>
          ))}
        </ul>
      </div>

      <div className="mt-8">
        <h2 className="font-black text-lg mb-3">{t.history}</h2>
        {(!data.commissions || data.commissions.length === 0) ? (
          <p className="text-sm text-slate-400">{t.empty}</p>
        ) : (
          <div className="border border-slate-200 divide-y divide-slate-100">
            {data.commissions.map((c, i) => (
              <div key={i} className="flex items-center justify-between px-4 py-2.5 text-sm" data-testid={`ref-commission-${i}`}>
                <span className="text-slate-600"><span className="font-mono text-slate-400">{(c.created_at || "").slice(0, 10)}</span> · {c.note || c.type}</span>
                <span className="font-black text-emerald-700">€{c.amount_eur}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
